import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useAwardWithBooks } from '@/hooks/useAwards';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { ArrowLeft, ExternalLink, Bell, BellOff, CheckCircle2 } from 'lucide-react';
import { CoverImage } from '@/components/CoverImage';
import { SaveToTBRButton } from '@/components/SaveToTBRButton';
import { Skeleton } from '@/components/ui/skeleton';
import { useFollowAward, useUnfollowAward, useAwardFollows } from '@/hooks/useAwardFollows';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export default function AwardDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: award, isLoading } = useAwardWithBooks(id!);
  const { data: follows = [] } = useAwardFollows();
  const followAward = useFollowAward();
  const unfollowAward = useUnfollowAward();

  // Fetch user's read books
  const { data: readBooks } = useQuery({
    queryKey: ['user-read-books', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_books')
        .select('book_id')
        .eq('user_id', user.id)
        .eq('shelf', 'read');
      
      if (error) throw error;
      return data?.map(ub => ub.book_id) || [];
    },
    enabled: !!user?.id,
  });

  const readBookIds = new Set(readBooks || []);
  const isFollowing = award ? follows.some(f => f.prize_name === award.prize_name) : false;

  const handleFollowToggle = () => {
    if (!user || !award) return;
    if (isFollowing) {
      unfollowAward.mutate(award.prize_name);
    } else {
      followAward.mutate(award.prize_name);
    }
  };

  if (isLoading) {
    return (
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <Skeleton className="w-32 h-10 mb-8" />
        <Skeleton className="w-full h-64" />
      </main>
    );
  }

  if (!award) {
    return (
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <p className="text-center text-muted-foreground">Award not found</p>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-8 max-w-6xl">
        <Link to="/awards">
          <Button variant="ghost" className="mb-6 gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Awards
          </Button>
        </Link>

        {/* Award Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">{award.prize_name}</h1>
              <div className="flex gap-2 mb-4">
                <Badge variant="secondary">{award.region}</Badge>
                <Badge variant="outline">{award.genre_focus}</Badge>
                <Badge>{award.year}</Badge>
              </div>
              <p className="text-lg text-muted-foreground">{award.description}</p>
            </div>
            {user && (
              <Button
                onClick={handleFollowToggle}
                variant={isFollowing ? 'default' : 'outline'}
                className="gap-2"
              >
                {isFollowing ? (
                  <>
                    <BellOff className="w-4 h-4" />
                    Following
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    Follow
                  </>
                )}
              </Button>
            )}
          </div>
          
          {award.link && (
            <a href={award.link} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="gap-2">
                <ExternalLink className="w-4 h-4" />
                Official Website
              </Button>
            </a>
          )}
        </div>

        <Tabs defaultValue="this-year" className="w-full">
          <TabsList>
            <TabsTrigger value="this-year">This Year</TabsTrigger>
            <TabsTrigger value="about">About the Prize</TabsTrigger>
          </TabsList>

          <TabsContent value="this-year" className="mt-6">
            {award.shortlist_books.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {award.shortlist_books.map((book) => (
                  <AwardBookCard 
                    key={book.id} 
                    book={book} 
                    isRead={readBookIds.has(book.id)}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <p className="text-center text-muted-foreground">
                    {award.genre_focus === 'lifetime' 
                      ? `Notable works for ${award.year} winner will be added soon`
                      : `Shortlist not yet announced for ${award.year}`
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="about" className="mt-6">
            <Card>
              <CardContent className="pt-6">
                <p className="text-lg mb-4">{award.description}</p>
                <div className="space-y-2 text-muted-foreground">
                  <p><strong>Region:</strong> {award.region}</p>
                  <p><strong>Focus:</strong> {award.genre_focus}</p>
                  {award.shortlist_date && (
                    <p><strong>{award.genre_focus === 'lifetime' ? 'Announced' : 'Shortlist announced'}:</strong> {new Date(award.shortlist_date).toLocaleDateString()}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
  );
}

function AwardBookCard({ book, isRead }: { book: any; isRead: boolean }) {
  const { isSaved, toggleSave, loading } = useSaveToTBR(
    book.id, 
    undefined, 
    { 
      title: book.title, 
      authors: book.authors,
      isbn13: book.isbn13,
      isbn10: book.isbn10 
    }
  );

  return (
    <Link to={`/book/${book.id}`}>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full relative">
        {isRead && (
          <div className="absolute top-2 right-2 z-10">
            <Badge 
              variant="default" 
              className="bg-green-600 hover:bg-green-700 flex items-center gap-1 shadow-lg"
            >
              <CheckCircle2 className="w-3 h-3" />
              Read
            </Badge>
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative">
              <CoverImage
                bookId={book.id}
                title={book.title}
                author={book.authors[0]}
                coverUrl={book.cover_url}
                size="md"
                className="w-24 h-36 rounded shadow-sm flex-shrink-0"
              />
              {isRead && (
                <div className="absolute inset-0 bg-green-600/20 rounded flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600 drop-shadow-lg" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold line-clamp-2 mb-1">{book.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {book.authors.join(', ')}
              </p>
              <div className="mt-3" onClick={(e) => e.preventDefault()}>
                <SaveToTBRButton
                  isSaved={isSaved}
                  onToggle={toggleSave}
                  loading={loading}
                  size="sm"
                  variant="outline"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
